/**
 * analytics.ts — web-client pageview tracking
 * Fire-and-forget POST to /api/v1/track/pageview (Log DB)
 * Captures: path, full_url, session, device, timezone, user identity (logged-in)
 */

const API_BASE = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? 'https://hub.boox.app/api/v1';
const APP_NAME = 'web-client' as const;

function shouldSkipRemoteAnalytics(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';
  const usesDefaultHub = !import.meta.env?.VITE_API_BASE_URL && API_BASE.includes('hub.boox.app');
  return isLocalHost && usesDefaultHub;
}

interface SessionMeta { id: string; start: string }
function getOrCreateSession(): SessionMeta {
  const KEY = 'boox_sid';
  const START_KEY = 'boox_ss';
  try {
    let sid = sessionStorage.getItem(KEY);
    let start = sessionStorage.getItem(START_KEY);
    if (!sid) {
      sid = crypto.randomUUID();
      start = new Date().toISOString();
      sessionStorage.setItem(KEY, sid);
      sessionStorage.setItem(START_KEY, start!);
    }
    return { id: sid, start: start! };
  } catch {
    return { id: crypto.randomUUID(), start: new Date().toISOString() };
  }
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth ?? 1024;
  if (w <= 640) return 'mobile';
  if (w <= 1024) return 'tablet';
  return 'desktop';
}

export interface TrackOptions {
  userId?: string;
  userDisplayName?: string;
  tenantId?: string;
}

export function trackPageView(path?: string, opts?: TrackOptions): void {
  if (typeof window === 'undefined') return;
  if (shouldSkipRemoteAnalytics()) return;

  const session = getOrCreateSession();

  const body: Record<string, unknown> = {
    app: APP_NAME,
    path: path ?? window.location.pathname,
    full_url: window.location.href,
    referrer: document.referrer || undefined,
    session_id: session.id,
    session_start: session.start,
    device_type: getDeviceType(),
    screen_width: window.innerWidth,
    client_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  if (opts?.userId) body['user_id'] = opts.userId;
  if (opts?.userDisplayName) body['user_display_name'] = opts.userDisplayName;
  if (opts?.tenantId) body['tenant_id'] = opts.tenantId;

  fetch(`${API_BASE}/track/pageview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => { /* 静默失败 */ });
}

/**
 * trackEvent — 业务行为事件采集
 * 在用户完成真实操作时调用（创建Job、使用AI、审批、扫码等）
 * 后端会自动触发积分 + 徽章检查，无需前端额外处理
 *
 * @example
 *   trackEvent('hms.job', 'create', { outcome: 'success', metadata: { entity_id: job.id } })
 *   trackEvent('ai.chat', 'query', { metadata: { model: 'vx-gemini-3.1-flash-lite' } })
 */
export interface TrackEventOptions {
  outcome?: 'success' | 'error' | 'cancelled';
  metadata?: Record<string, unknown>;
  sessionId?: string;
}

export function trackEvent(
  feature: string,
  action: string,
  opts?: TrackEventOptions,
): void {
  if (typeof window === 'undefined') return;
  if (shouldSkipRemoteAnalytics()) return;

  const session = getOrCreateSession();

  // 需要带 Authorization header，从 localStorage 读 token
  let token: string | null = null;
  try {
    token = localStorage.getItem('boox_token') ||
            localStorage.getItem('access_token') ||
            sessionStorage.getItem('boox_token');
  } catch { /* ignore */ }

  const body: Record<string, unknown> = {
    app: APP_NAME,
    feature,
    action,
    outcome:    opts?.outcome  ?? 'success',
    metadata:   opts?.metadata ?? {},
    session_id: opts?.sessionId ?? session.id,
    device_type: getDeviceType(),
    client_tz:  Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  fetch(`${API_BASE}/track/event`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => { /* 静默失败 */ });
}
